'use client';

/**
 * Conversation list component for viewing all chat conversations
 */

import React, { useCallback } from 'react';
import type { SupportThread, ThreadId } from '../types/support-chat';
import { format, formatDistanceToNow } from 'date-fns';

interface ThreadListProps {
  threads: SupportThread[];
  currentThreadId: ThreadId | null;
  onSelectThread: (threadId: ThreadId) => void;
  onCreateNewThread: () => void;
  isLoading?: boolean;
}

export function ThreadList({
  threads,
  currentThreadId,
  onSelectThread,
  onCreateNewThread,
  isLoading = false,
}: ThreadListProps) {
  const handleSelectThread = useCallback(
    (threadId: ThreadId) => {
      onSelectThread(threadId);
    },
    [onSelectThread]
  );

  const sortedThreads = [...threads].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA; // Most recent first
  });

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
        }}
      >
        {sortedThreads.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280',
            }}
          >
            <div
              style={{
                fontSize: '48px',
                marginBottom: '16px',
              }}
            >
              💬
            </div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 500,
                marginBottom: '8px',
                color: '#374151',
              }}
            >
              No previous conversations
            </div>
            <div
              style={{
                fontSize: '14px',
                color: '#6b7280',
              }}
            >
              Start a new conversation to get help
            </div>
          </div>
        ) : (
          <div>
            {sortedThreads.map((thread) => {
              const isActive = currentThreadId === thread.threadId;
              const threadDate = new Date(thread.createdAt);
              const formattedDate = format(threadDate, 'MMM d, yyyy');
              const relativeDate = formatDistanceToNow(threadDate, { addSuffix: true });

              return (
                <button
                  key={thread.threadId}
                  onClick={() => handleSelectThread(thread.threadId)}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundColor: isActive ? '#eff6ff' : '#ffffff',
                    border: isActive ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isActive
                      ? '0 2px 4px rgba(37, 99, 235, 0.1)'
                      : '0 1px 2px rgba(0, 0, 0, 0.05)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive && !isLoading) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#2563eb' : '#111827',
                      }}
                    >
                      Support Chat
                    </div>
                    {isActive && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#2563eb',
                          fontWeight: 600,
                          backgroundColor: '#dbeafe',
                          padding: '2px 8px',
                          borderRadius: '12px',
                        }}
                      >
                        Active
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '4px',
                    }}
                  >
                    {formattedDate}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                    }}
                  >
                    {relativeDate}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          padding: '16px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
        }}
      >
        <button
          onClick={onCreateNewThread}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            opacity: isLoading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = '#1d4ed8';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }
          }}
        >
          + Start New Chat
        </button>
      </div>
    </div>
  );
}

